import { ChevronLeft, ChevronRight } from 'lucide-preact';
import { useMemo } from 'preact/hooks';
import { DAY_LABELS, MONTH_NAMES, buildCalendar, toYMD } from '../calendar.js';

interface CalendarGridProps {
  year: number;
  month: number;
  selectedYMD: string | null;
  minDate?: Date;
  maxDate?: Date;
  onSelectDay: (ymd: string) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}

// Month grid of day cells, restyled to the carii app dark-theme tokens.
export function CalendarGrid({
  year,
  month,
  selectedYMD,
  minDate,
  maxDate,
  onSelectDay,
  onPrevMonth,
  onNextMonth,
}: CalendarGridProps) {
  const days = useMemo(
    () => buildCalendar(year, month, { minDate, maxDate }),
    [year, month, minDate, maxDate],
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={onPrevMonth}
          aria-label="Previous month"
          className="w-8 h-8 rounded-lg bg-surface-mid flex items-center justify-center text-text hover:bg-surface-alt"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="text-xs uppercase tracking-wider text-text-muted font-semibold">
          {MONTH_NAMES[month]} {year}
        </span>
        <button
          type="button"
          onClick={onNextMonth}
          aria-label="Next month"
          className="w-8 h-8 rounded-lg bg-surface-mid flex items-center justify-center text-text hover:bg-surface-alt"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {DAY_LABELS.map((label) => (
          <div
            key={label}
            className="h-6 flex items-center justify-center text-[10px] text-text-muted uppercase tracking-wide"
          >
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {days.map((day) => {
          const ymd = toYMD(day.date);
          const isSelected = ymd === selectedYMD;
          return (
            <button
              key={ymd}
              type="button"
              aria-label={ymd}
              disabled={day.disabled}
              onClick={() => onSelectDay(ymd)}
              className={[
                'h-9 rounded-lg text-sm font-medium',
                day.disabled
                  ? 'opacity-25 cursor-not-allowed text-text-muted'
                  : isSelected
                    ? 'bg-primary text-on-primary cursor-pointer'
                    : 'bg-transparent text-text cursor-pointer hover:bg-surface-mid',
                !day.inCurrentMonth && !isSelected ? 'opacity-40' : '',
                day.isToday && !isSelected ? 'ring-1 ring-border-light' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {day.dayNumber}
            </button>
          );
        })}
      </div>
    </div>
  );
}
